import { Injectable } from '@angular/core';
import { catchError, Observable, Observer, of, shareReplay, Subject, tap } from 'rxjs';
import { HttpRequestService } from '../http-request/http-request.service';
//import { SessionService } from '../session/session.service';
//import { Config } from '../../config';
import { ChatbotQuery, DataPageDTO, IChatbotApiService, IChatbotHistoryApiService, LazyLoadQueryParams, PromptQuotaStatus, SessionDTO, SessionHistoryDTO, SessionResponse } from 'sabis-chatbot';
//import { LoggingService } from '../logging/logging.service';
import { chatbotSettings } from './chatbot-settings.model';
import { AppConfigService } from '../app-config/app-config.service';
import { UserSessionService } from '../user-session/user-session.service';
import { Router } from '@angular/router';
import { ChatbotHttpRequestService } from '../http-request/chatbot-http-request.service';


/**
 * ChatbotService handles chatbot operations like API communication,
 * health checks, and streaming responses via Server-Sent Events (SSE).
 */
@Injectable({
  providedIn: 'root',
})
export class ChatbotApiService implements IChatbotApiService, IChatbotHistoryApiService {
  private _chatbotAvailability$: Observable<boolean>;
  private _lastPromptQuotaStatus: PromptQuotaStatus | null = null;
  private _ebookWebApplicationId: string = 'c1b2c3d6-0003-0001-0001-000000000003';

  constructor(
    private _http: HttpRequestService,
    private _configService: AppConfigService,
    private _userSessionService: UserSessionService,
    private _router: Router,
    private _chatbotHttp: ChatbotHttpRequestService
  ) {
    // Subscribe to logout to clear cached data
    this._userSessionService.sessionStateChanged().subscribe(isActive => {
      if (!isActive) {
        this.clearChatbotCache();
      }
    });
  }

  /** Clears the chatbot availability cache */
  private clearChatbotCache(): void {
    this._chatbotAvailability$ = null;
    this._lastPromptQuotaStatus = null;
  }

  /** Performs a check if the user has access to chatbot */
  public checkChatbotAvailability(): Observable<boolean> {
    if (!this._chatbotAvailability$) {
      return this._chatbotHttp.get('/chatbot/has-access').pipe(
        catchError((error) => {
          console.error('API error:', error);
          return of({ isChatbotAllowed: false });
        }),
        shareReplay(1)
      );
    }
    return this._chatbotAvailability$;
  }

  /** Performs a health check on the chatbot backend. */
  public healthCheck(): Observable<boolean> {
    return this._chatbotHttp.get('/chatbot/healthcheck').pipe(
      catchError((error) => {
        console.error('API error:', error);
        //this._loggingService.error('healthCheck error', error, 'SabisChatbot');
        return of(false);
      })
    );
  }

  /** Generate a new SessionId for the current user chat session */
  public generateSessionId(): Observable<SessionResponse> {
    return this._chatbotHttp.post('/chatbot/sessions', { applicationPublicId: this._ebookWebApplicationId });
  }


  /**
   * Streams chatbot responses using Server-Sent Events (SSE).
   * Handles connection setup, timeout, and response parsing.
   * @param message - The user's message to send to the chatbot.
   * @param sessionId - The session identifier for tracking conversation context.
   * @param mode - The chatbot mode.
   * @returns Observable emitting streamed chatbot responses as strings.
   */
  public streamLLMResponse(
    message: string,
    sessionId: string,
    mode: string
  ): Observable<string> {
    const chatbotQuery = this.createChatbotQuery(message, sessionId, mode);

    return new Observable((observer) => {

      // Check session validity first (same as FAB does)
      if (!this._userSessionService.isSessionActive()) {
        observer.error('Session expired. Redirecting to login...');
        this._router.navigateByUrl('/login'); // or AppRoutes.login
        return;
      }

      if (!navigator.onLine) {
        observer.error('You are offline.');
        return;
      }

      const controller = new AbortController();
      const TIMEOUT_MS = 60000;
      const { resetTimeout, clearTimeout } = this.setupTimeout(
        controller,
        observer,
        TIMEOUT_MS
      );

      fetch(this._chatbotHttp.resolveMasterUrl('/chatbot/query'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(chatbotQuery),
        signal: controller.signal,
        keepalive: true,
      })
        .then((response) => {
          if (!response.ok) {
            clearTimeout();
            observer.error(
              `HTTP error ${response.status}: ${response.statusText}`
            );
            return;
          }

          const reader = response.body?.getReader();
          if (reader) {
            this.processStream(
              reader,
              observer,
              controller,
              resetTimeout,
              clearTimeout
            );
          } else {
            observer.error('No response body.');
          }
        })
        .catch((err) => {
          clearTimeout();
          observer.error('Fetch error: ' + err.message + '\n' + err.stack);
          //this._loggingService.error('Fetch error: ', err, 'SabisChatbot');
        });

      return () => {
        clearTimeout();
        controller.abort();
      };
    });
  }

  public getPromptsQuota(sessionId: string): Observable<PromptQuotaStatus> {
    return of(this._lastPromptQuotaStatus!);
  }

  public getSessions(searchText: string, params: LazyLoadQueryParams): Observable<DataPageDTO<SessionHistoryDTO>> {
    return this._chatbotHttp.post('/chat-history/sessions', {
      searchText: searchText,
      queryParams: params
    }).pipe(
      tap(r => {
        // Convert date strings to Date objects
        r.rows.forEach(session => {
          session.date = new Date(session.date);
        });
      })
    );;
  }

  public getSession(sessionId: number): Observable<SessionDTO> {
    return this._chatbotHttp.get(`/chat-history/sessions/${sessionId}`).pipe(
      catchError((error) => {
        console.error('API error:', error);
        return of(0);
      })
    );
  }

  public updateSessionTitle(sessionId: number, newTitle: string): Observable<void> {
    return this._chatbotHttp.post(`/chat-history/sessions/${sessionId}/title`, {
      newTitle: newTitle,
    });
  }

  /**
   * Constructs the chatbot query payload.
   * @param message - The user's message.
   * @param sessionId - The session identifier.
   * @param mode - The chatbot mode.
   * @returns ChatbotQuery object containing the message and session ID.
   */
  private createChatbotQuery(message: string, sessionId: string, mode: string): ChatbotQuery {
    return {
      query: message,
      sessionId: sessionId,
      mode: mode,
      applicationPublicId: this._ebookWebApplicationId
    };
  }

  /**
   * Builds the HTTP headers for the chatbot request.
   * Includes authorization if a token is available.
   * @returns HeadersInit object for the fetch request.
   */
  private getHeaders(): HeadersInit {
    const accessToken = this._userSessionService.getSessionToken();
    return {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(accessToken && {
        Authorization: `Bearer ${accessToken}`,
      }),
    };
  }

  /**
   * Sets up timeout logic to abort the request if no data is received within the specified time.
   * @param controller - AbortController to cancel the request.
   * @param observer - Observable observer to emit errors.
   * @param timeoutMs - Timeout duration in milliseconds.
   * @returns Functions to reset and clear the timeout.
   */
  private setupTimeout(
    controller: AbortController,
    observer: Observer<string>,
    timeoutMs: number
  ): { resetTimeout: () => void; clearTimeout: () => void } {
    let timeoutHandle: any;

    const resetTimeout = () => {
      clearTimeout(timeoutHandle);
      timeoutHandle = setTimeout(() => {
        controller.abort();
        observer.error('Connection lost: no data received within timeout.');
      }, timeoutMs);
    };

    return {
      resetTimeout,
      clearTimeout: () => clearTimeout(timeoutHandle),
    };
  }

  /**
   * Reads and processes the streamed response from the chatbot.
   * Parses SSE chunks and emits valid text responses to the observer.
   * @param reader - Stream reader from the response body.
   * @param observer - Observable observer to emit data.
   * @param controller - AbortController to handle cancellation.
   * @param resetTimeout - Function to reset the timeout.
   * @param clearTimeout - Function to clear the timeout.
   */
  private processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    observer: Observer<string>,
    controller: AbortController,
    resetTimeout: () => void,
    clearTimeout: () => void
  ): void {
    const decoder = new TextDecoder();
    let buffer = '';
    let hasEmittedData = false;

    controller.signal.addEventListener('abort', () => {
      reader.cancel().catch(() => { });
    });

    const read = () => {
      reader
        .read()
        .then(({ done, value }) => {
          if (done) {
            clearTimeout();
            if (!hasEmittedData) {
              observer.error('Stream finished without emitting any data.');
            } else {
              observer.complete();
            }
            return;
          }

          resetTimeout();
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          lines.forEach((line) => {
            if (line.startsWith('data:')) {
              const raw = line.slice(5).trim();
              if (
                raw &&
                raw !== '[DONE]' &&
                !raw.startsWith('[SessionStatus]') &&
                !raw.startsWith('[UserMessage]')
              ) {
                try {
                  const { text } = JSON.parse(raw);
                  if (text) {
                    hasEmittedData = true;
                    observer.next(text);
                  }
                } catch (err) {
                  console.error('Failed to parse response chunk:', raw);
                  observer.error('Failed to parse response chunk.');
                  //this._loggingService.error(
                  //  'Failed to parse PromptQuotaStatus: ',
                  //  err,
                  //  'SabisChatbot'
                  //);
                }
              } else if (raw.startsWith('[SessionStatus]')) {
                this.handleSessionStatus(raw);
              }
            }
          });

          read();
        })
        .catch((err) => {
          clearTimeout();
          observer.error(
            'Stream reading error: ' + err.message + '\n' + err.stack
          );
        });
    };

    resetTimeout();
    read();
  }

  /**
   * Parses a raw string containing session status information and emits the deserialized
   * PromptQuotaStatus object to the quota service.
   *
   * @param raw - The raw string expected to contain a JSON object prefixed with [SessionStatus].
   */
  private handleSessionStatus(raw: string): void {
    if (raw.startsWith('[SessionStatus]')) {
      const jsonMatch = raw.match(/\{.*\}/);
      if (jsonMatch) {
        try {
          this._lastPromptQuotaStatus = JSON.parse(jsonMatch[0]);
        } catch (error) {
          console.error('Failed to parse PromptQuotaStatus:', error);
        }
      }
    }
  }
}
